/**
 * Ledger keys of a recursive ACL (`acl`) transfer job — S8 Task 8.2 (finding S8-R2-03). Every record's `item` is
 * built HERE from positive integers: `acl:<epoch>:traversal`, `acl:<epoch>:failure:<seq>`, `acl:<epoch>:omitted`.
 * Disjointness does not rest on the spelling (Azure accepts ':' in names, so a path CAN read `acl:1:traversal`): it
 * rests on paths never being ledger items — the path lives only in `HnsAclFailure.path` / `record.source`, and every
 * surface finds it through `aclFailureForKey`, never by treating a key as a path.
 */
import type { HnsAclFailure, TransferJobAcl } from '../../types/azureStorage';

export type AclRecordRole =
  | { role: 'traversal'; epoch: number }
  | { role: 'failure'; epoch: number; seq: number }
  | { role: 'omitted'; epoch: number };

const positive = (n: number, what: string): number => {
  if (!Number.isSafeInteger(n) || n < 1) throw new RangeError(`${what} must be a positive integer.`);
  return n;
};

export function aclRecordKey(role: AclRecordRole): string {
  const epoch = positive(role.epoch, 'epoch');
  if (role.role === 'failure') return `acl:${epoch}:failure:${positive(role.seq, 'seq')}`;
  return `acl:${epoch}:${role.role}`;
}

const KEY = /^acl:([1-9]\d*):(traversal|omitted|failure:([1-9]\d*))$/u;

/** The exact inverse of `aclRecordKey`; `undefined` for anything else (a path, a malformed or unsafe number). */
export function parseAclRecordKey(item: string): AclRecordRole | undefined {
  const match = KEY.exec(item);
  if (!match) return undefined;
  const epoch = Number(match[1]);
  if (!Number.isSafeInteger(epoch)) return undefined;
  if (match[2] === 'traversal' || match[2] === 'omitted') return { role: match[2], epoch };
  const seq = Number(match[3]);
  return Number.isSafeInteger(seq) ? { role: 'failure', epoch, seq } : undefined;
}

/** The listed failure a record key names, in the job's CURRENT epoch only; never falls back to reading the key as a path. */
export function aclFailureForKey(job: { acl?: Pick<TransferJobAcl, 'epoch' | 'failures'> }, item: string): HnsAclFailure | undefined {
  const key = parseAclRecordKey(item);
  if (!key || key.role !== 'failure' || !job.acl || key.epoch !== job.acl.epoch) return undefined;
  return job.acl.failures.find((f) => f.seq === key.seq);
}
